import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
  fetchPlatformDispatchers,
  updatePlatformOrganization,
} from 'api/platform';
import TabNote from 'containers/Platform/TabNote';
import {
  formItemStyle,
  formTooltip,
  optionalLabel,
  platformFormClassName,
  sectionCardProps,
  sectionStackGap,
  tabContentStyle,
  tipLines,
  tipList,
} from 'containers/Platform/labels';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

const { Text, Paragraph } = Typography;

function CredentialMeta({ meta }) {
  if (!meta?.is_set) return <Text type="secondary">Not Configured</Text>;
  if (meta.is_valid === false) return <Text type="danger">Invalid value — replace it</Text>;
  return (
    <Text>
      Configured
      {' '}
      ******
      <Text strong style={{ fontWeight: 700 }}>{meta.last4}</Text>
    </Text>
  );
}

function SectionTitle({ title, tag }) {
  return (
    <Flex align="center" gap={8} wrap="wrap">
      <span>{title}</span>
      {tag}
    </Flex>
  );
}

function expediteStatus({ org, sharedDispatcher }) {
  const hasOrgToken = Boolean(org.expedite_api_token_meta?.is_set);
  const hasOrgSecret = Boolean(org.expedite_api_secret_meta?.is_set);
  if (hasOrgToken || hasOrgSecret) {
    return {
      tag: <Tag color="success">Connected - via Expedite dedicated account</Tag>,
      detail: 'Org-level token/secret override the shared Expedite dispatcher.',
    };
  }
  if (sharedDispatcher) {
    const name = sharedDispatcher.name || 'Expedite';
    return {
      tag: <Tag color="success">Connected - via Expedite shared account</Tag>,
      detail: `${name}${sharedDispatcher.is_platform_default ? ' (platform default)' : ''}. Leave credentials blank to keep using the shared account.`,
    };
  }
  return {
    tag: <Tag>Shared Expedite not available</Tag>,
    detail: 'No active platform-default Expedite dispatcher is configured. Set one in Django admin or enter org-level credentials below.',
  };
}

function fulflldStatus(org) {
  const hasAppId = Boolean(org.fulflld_app_id_meta?.is_set);
  const hasAppSecret = Boolean(org.fulflld_app_secret_meta?.is_set);
  if (!hasAppId && !hasAppSecret) {
    // AC: empty credentials → "Not configured" (not "Disabled").
    if (!org.fulflld_enabled) {
      return {
        tag: <Tag>Not configured</Tag>,
        detail: 'Enter app ID and secret, enable FULFLLD, then save to connect.',
      };
    }
    return {
      tag: <Tag color="warning">Not configured</Tag>,
      detail: 'FULFLLD is on, but app ID and secret are missing. Enter them and save to connect.',
    };
  }
  if (!org.fulflld_enabled) {
    return {
      tag: <Tag>Disabled</Tag>,
      detail: 'FULFLLD is off. Credentials are still saved; enable and save to refresh a bearer token.',
    };
  }
  if (org.fulflld_token_fetch_failed) {
    return {
      tag: <Tag color="error">Credentials invalid</Tag>,
      detail: 'Last token fetch failed. Replace the app ID/secret and save to re-validate.',
    };
  }
  if (org.has_valid_fulflld_token) {
    return {
      tag: <Tag color="success">Connected</Tag>,
      detail: 'FULFLLD credentials are saved and a bearer token is available.',
    };
  }
  return {
    tag: <Tag color="warning">Configured</Tag>,
    detail: 'Credentials are saved. Token will refresh on the next FULFLLD request.',
  };
}

function fulflldDisableDefaultTargetLabel(org) {
  // Mirrors PlatformOrganizationUpdateSerializer disable reset (NXS-2692 CR #3).
  if (org.dispatcher || org.dispatcher_info) return 'Expedite';
  if (org.is_catering) return 'self-delivery';
  return 'none';
}

function DisableFulflldModal({ open, org, confirmLoading, onConfirm, onCancel }) {
  const hasCreds = Boolean(org.fulflld_app_id_meta?.is_set || org.fulflld_app_secret_meta?.is_set);
  const hasToken = Boolean(org.has_valid_fulflld_token);
  const defaultIsFulflld = org.default_delivery_type === 'fulflld';
  const defaultTarget = fulflldDisableDefaultTargetLabel(org);

  return (
    <Modal
      title="Disable FULFLLD?"
      open={open}
      width={480}
      destroyOnClose
      maskClosable={false}
      keyboard={!confirmLoading}
      closable={!confirmLoading}
      onCancel={onCancel}
      footer={[
        <Button key="keep" disabled={confirmLoading} onClick={onCancel}>
          No, Keep it enabled
        </Button>,
        <Button key="disable" type="primary" danger loading={confirmLoading} onClick={onConfirm}>
          Yes, Disable it
        </Button>,
      ]}
    >
      <div>
        <Paragraph style={{ marginBottom: 8 }}>
          FULFLLD is currently
          {' '}
          <Text strong>enabled</Text>
          {hasCreds ? ' with app credentials saved' : ''}
          {hasToken ? ' and a valid bearer token cached' : ''}
          .
        </Paragraph>
        <Paragraph style={{ marginBottom: 8 }}>If you disable it and save:</Paragraph>
        <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
          <li>FULFLLD will no longer be available as a delivery option for this client</li>
          <li>The cached bearer token will be cleared so Nibble cannot call FULFLLD for this org</li>
          <li>
            {defaultIsFulflld ? (
              <>
                The console default delivery type will be moved off FULFLLD to
                {' '}
                <Text strong>{defaultTarget}</Text>
              </>
            ) : (
              <>
                If the console default delivery type is FULFLLD, it will be moved off FULFLLD
                (to Expedite, self-delivery, or none)
              </>
            )}
          </li>
          <li>App ID and app secret stay saved so you can re-enable later without re-entering them</li>
          <li>Existing in-flight FULFLLD deliveries are not cancelled by this toggle</li>
        </ul>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Re-enabling and saving will fetch a new bearer token using the stored credentials.
        </Paragraph>
      </div>
    </Modal>
  );
}

export default function DispatchersTab({ org, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [sharedDispatcher, setSharedDispatcher] = useState(null);
  const pendingSaveRef = useRef(null);

  useEffect(() => {
    if (org.dispatcher_info) {
      setSharedDispatcher(org.dispatcher_info);
      return undefined;
    }
    fetchPlatformDispatchers({ dispatcher_type: 'expedite' })
      .then((res) => {
        const rows = res.results || [];
        const matched = rows.find((d) => d.id === org.dispatcher)
          || rows.find((d) => d.is_platform_default)
          || rows[0]
          || null;
        setSharedDispatcher(matched);
      })
      .catch((e) => captureException(e));
    return undefined;
  }, [org.dispatcher, org.dispatcher_info]);

  useEffect(() => {
    // Don't clobber in-progress disable confirm with a stale org.fulflld_enabled=true.
    if (disableModalOpen) return;
    form.setFieldsValue({
      expedite_client_name: org.expedite_client_name || '',
      expedite_api_url: org.expedite_api_url || '',
      expedite_api_token: '',
      expedite_api_secret: '',
      sync_dispatcher_fees_to_customer_orders: org.sync_dispatcher_fees_to_customer_orders,
      min_dispatch_lead_time_in_minutes: org.min_dispatch_lead_time_in_minutes,
      fulflld_enabled: Boolean(org.fulflld_enabled),
      fulflld_app_id: '',
      fulflld_app_secret: '',
    });
  }, [org, form, disableModalOpen]);

  const persistDispatchers = (values) => {
    const payload = {
      ...values,
      // Always send an explicit boolean — omitting/undefined leaves the org enabled.
      fulflld_enabled: Boolean(values.fulflld_enabled),
    };
    ['expedite_api_token', 'expedite_api_secret', 'fulflld_app_id', 'fulflld_app_secret'].forEach((key) => {
      if (!payload[key]) delete payload[key];
    });
    setSaving(true);
    updatePlatformOrganization(org.id, payload)
      .then((updated) => {
        message.success('Dispatchers saved');
        setDisableModalOpen(false);
        pendingSaveRef.current = null;
        form.setFieldsValue({
          expedite_api_token: '',
          expedite_api_secret: '',
          fulflld_app_id: '',
          fulflld_app_secret: '',
          fulflld_enabled: Boolean(updated.fulflld_enabled),
        });
        onSaved(updated);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setSaving(false));
  };

  const onFinish = (values) => {
    // Confirm disable on save — not on toggle — and force false in the PATCH body.
    const wantsDisable = Boolean(org.fulflld_enabled) && values.fulflld_enabled === false;
    if (wantsDisable) {
      pendingSaveRef.current = values;
      setDisableModalOpen(true);
      return;
    }
    persistDispatchers(values);
  };

  const onConfirmDisable = () => {
    const values = pendingSaveRef.current || form.getFieldsValue(true);
    persistDispatchers({ ...values, fulflld_enabled: false });
  };

  const onCancelDisable = () => {
    if (saving) return;
    setDisableModalOpen(false);
    pendingSaveRef.current = null;
    form.setFieldsValue({ fulflld_enabled: true });
  };

  const expedite = expediteStatus({ org, sharedDispatcher });
  const fulflld = fulflldStatus(org);

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <DisableFulflldModal
        open={disableModalOpen}
        org={org}
        confirmLoading={saving}
        onConfirm={onConfirmDisable}
        onCancel={onCancelDisable}
      />

      <TabNote title="Dispatchers">
        Configure Expedite and FULFLLD credentials for this client. Blank secret fields keep the
        current value. Org-level Expedite credentials override the shared dispatcher; FULFLLD is
        per-client and starts as not configured.
      </TabNote>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className={platformFormClassName}
      >
        <Space direction="vertical" size={sectionStackGap} style={{ width: '100%' }}>
          <Card
            {...sectionCardProps}
            styles={{ body: { padding: '8px 20px 16px' } }}
            title={<SectionTitle title="Expedite" tag={expedite.tag} />}
          >
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4 }}>
                {expedite.detail}
              </Text>
            </div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="expedite_client_name"
                  label={optionalLabel('Client name')}
                  style={formItemStyle}
                  tooltip={formTooltip('Client name', tipLines(
                    'Brand identifier sent to Expedite.',
                    'If blank, the organization name is used.',
                  ))}
                >
                  <Input autoComplete="off" placeholder={org.name || 'Organization name'} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="expedite_api_url"
                  label={optionalLabel('API URL')}
                  style={formItemStyle}
                  tooltip={formTooltip(
                    'API URL',
                    'Leave blank to use the shared / global Expedite API URL.',
                  )}
                >
                  <Input
                    autoComplete="off"
                    placeholder={org.effective_expedite_api_url || 'Shared Expedite URL'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="expedite_api_token"
                  label={optionalLabel('API token')}
                  style={formItemStyle}
                  tooltip={formTooltip('API token', tipList([
                    'Leave blank to use the shared dispatcher token',
                    'Enter a value to override for this client',
                  ]))}
                  extra={<CredentialMeta meta={org.expedite_api_token_meta} />}
                >
                  <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="expedite_api_secret"
                  label={optionalLabel('API secret')}
                  style={formItemStyle}
                  tooltip={formTooltip('API secret', tipList([
                    'Leave blank to use the global Expedite secret',
                    'Enter a value to override for this client',
                  ]))}
                  extra={<CredentialMeta meta={org.expedite_api_secret_meta} />}
                >
                  <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="min_dispatch_lead_time_in_minutes"
                  label="Dispatch lead time (minutes)"
                  style={formItemStyle}
                  tooltip={formTooltip(
                    'Dispatch lead time (minutes)',
                    'Minimum minutes before pickup when creating an Expedite delivery.',
                  )}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="sync_dispatcher_fees_to_customer_orders"
                  label="Use Expedite quote as customer fee"
                  valuePropName="checked"
                  style={formItemStyle}
                  tooltip={formTooltip('Use Expedite quote as customer fee', tipLines(
                    'On: customer fee uses the Expedite quote.',
                    "Off: uses this client's Nibble delivery fee.",
                  ))}
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            {...sectionCardProps}
            styles={{ body: { padding: '8px 20px 16px' } }}
            title={<SectionTitle title="FULFLLD" tag={fulflld.tag} />}
          >
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4 }}>
                {fulflld.detail}
              </Text>
            </div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="API URL"
                  style={formItemStyle}
                  tooltip={formTooltip('API URL', tipLines(
                    'Platform FULFLLD host used for API requests.',
                    'Not configurable per client (unlike Expedite).',
                  ))}
                >
                  <Input
                    value={org.effective_fulflld_api_url || '—'}
                    disabled
                    readOnly
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fulflld_app_id"
                  label="App ID"
                  style={formItemStyle}
                  tooltip={formTooltip('App ID', tipLines(
                    'Provided by FULFLLD.',
                    'Required with App secret to authenticate.',
                  ))}
                  extra={<CredentialMeta meta={org.fulflld_app_id_meta} />}
                >
                  <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fulflld_app_secret"
                  label="App secret"
                  style={formItemStyle}
                  tooltip={formTooltip('App secret', tipLines(
                    'Provided by FULFLLD.',
                    'Required with App ID to authenticate.',
                  ))}
                  extra={<CredentialMeta meta={org.fulflld_app_secret_meta} />}
                >
                  <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="fulflld_enabled"
                  label="Enable FULFLLD"
                  valuePropName="checked"
                  style={formItemStyle}
                  tooltip={formTooltip('Enable FULFLLD', tipLines(
                    'Turns FULFLLD on as a dispatcher for this client.',
                    'Requires App ID and App secret.',
                    'Disabling clears the cached bearer token on save and, if the console default delivery type is FULFLLD, moves that default off FULFLLD (to Expedite, self-delivery, or none).',
                  ))}
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Button type="primary" htmlType="submit" loading={saving}>
            Save dispatchers
          </Button>
        </Space>
      </Form>
    </Space>
  );
}
